import logger from "../utils/logger";
import { useEffect, useRef, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "../utils/auth";
import { isBusy } from "../modules/connect/calling/callState";
import { logAuthDiagnostic } from "../utils/blankPageDiagnostics";
import {
  ACTIVITY_STORAGE_KEY,
  recordSharedActivity,
  getSharedLastActivityTime,
  hasActiveMeetingLease,
  subscribeAuthSync,
  AUTH_SYNC_EVENTS,
} from "../utils/authSyncChannel";

export { ACTIVITY_STORAGE_KEY };

const ACTIVITY_THROTTLE_MS = 15000; // Throttle shared storage writes to at most once per 15s
const INACTIVITY_CHECK_INTERVAL_MS = 10000; // Check inactivity delta every 10s

export const DEFAULT_INACTIVITY_TIMEOUT_MINUTES = 15;
export const WARNING_DURATION_SECONDS = 60;
export const INACTIVITY_LOGOUT_LOCK = "synexos_inactivity_logout";

/**
 * Moment the inactivity warning expires, derived from the shared last-activity time so all
 * tabs agree. A tab that only notices late (throttled background timer) still gets at least
 * a short visible countdown rather than an instant logout.
 */
export function getInactivityDeadline(lastActivityTime, timeoutMs, now = Date.now()) {
  const shared = lastActivityTime + timeoutMs + WARNING_DURATION_SECONDS * 1000;
  // Shared deadline still ahead → every tab agrees on it. Already passed (device sleep,
  // throttled background timer) → give the full warning, never a shorter one than before.
  return shared > now ? shared : now + WARNING_DURATION_SECONDS * 1000;
}

/**
 * Validates and converts the configured environment variable timeout into milliseconds.
 * Defaults to 15 minutes (900,000 ms) if unconfigured or invalid.
 */
export function getInactivityTimeoutMs() {
  const raw = process.env.REACT_APP_INACTIVITY_TIMEOUT_MINUTES;
  if (raw === undefined || raw === null || raw === "") {
    return DEFAULT_INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    logger.warn(
      `[InactivityTimeout] Invalid REACT_APP_INACTIVITY_TIMEOUT_MINUTES value ("${raw}"). Falling back to default ${DEFAULT_INACTIVITY_TIMEOUT_MINUTES} minutes.`
    );
    return DEFAULT_INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
  }

  return parsed * 60 * 1000;
}

/**
 * Checks whether the current window/tab is actively participating in Connect / Meet / Huddle.
 */
export function isUserInActiveConnectMeet(pathname = typeof window !== "undefined" ? window.location.pathname : "") {
  const isConnectRoute = pathname.startsWith("/connect") || pathname.startsWith("/meet");
  let callBusy = false;
  try {
    callBusy = typeof isBusy === "function" ? Boolean(isBusy()) : false;
  } catch {
    callBusy = false;
  }
  return isConnectRoute || callBusy;
}

/**
 * Custom hook providing automatic inactivity logout for SynexOS with:
 * 1. Shared cross-tab activity tracking via localStorage + BroadcastChannel.
 * 2. Cross-tab Active Meeting exemption (an active meeting in ANY tab suppresses auto-logout for the whole session).
 * 3. Safe background tab wake-up (always reads latest shared activity and meeting state before evaluating timeout).
 * 4. 60-second inactivity warning popup with countdown before executing logout.
 *
 * @param {Object} options
 * @param {Object|null} options.user Current user session object
 * @param {Function} [options.onLogout] Optional callback invoked on auto-logout
 * @returns {{ isWarningOpen: boolean, countdown: number, stayLoggedIn: Function }}
 */
export function useInactivityTimeout({ user, onLogout } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const lastThrottleWriteRef = useRef(0);
  const isLoggingOutRef = useRef(false);

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_DURATION_SECONDS);
  const isWarningOpenRef = useRef(false);
  const countdownEndRef = useRef(null);

  const timeoutMs = getInactivityTimeoutMs();

  const recordActivity = useCallback((forced = false) => {
    recordSharedActivity(forced, lastThrottleWriteRef, ACTIVITY_THROTTLE_MS);
  }, []);

  const dismissWarning = useCallback(() => {
    if (isWarningOpenRef.current) {
      setIsWarningOpen(false);
      isWarningOpenRef.current = false;
      countdownEndRef.current = null;
      setCountdown(WARNING_DURATION_SECONDS);
    }
  }, []);

  const stayLoggedIn = useCallback(() => {
    dismissWarning();
    recordActivity(true);
    logAuthDiagnostic("inactivity_stay_logged_in_clicked");
  }, [dismissWarning, recordActivity]);

  const performLogout = useCallback(() => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    // Safety re-check: verify if an active meeting lease or recent activity exists before executing logout
    if (hasActiveMeetingLease() || isUserInActiveConnectMeet(window.location.pathname)) {
      logAuthDiagnostic("inactivity_logout_aborted_active_meeting");
      isLoggingOutRef.current = false;
      dismissWarning();
      return;
    }

    const latestSharedActivity = getSharedLastActivityTime();
    if (timeoutMs && Date.now() - latestSharedActivity < timeoutMs) {
      logAuthDiagnostic("inactivity_logout_aborted_recent_activity_in_other_tab", {
        elapsed: Date.now() - latestSharedActivity,
        timeoutMs,
      });
      isLoggingOutRef.current = false;
      dismissWarning();
      return;
    }

    dismissWarning();

    logAuthDiagnostic("inactivity_executing_logout", {
      pathname: window.location.pathname,
    });

    const finishLocally = () => {
      if (typeof onLogout === "function") {
        onLogout();
      }
      // Redirect to login page
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth/login")) {
        navigate("/auth/login", { replace: true });
      }
    };

    // Every tab reaches the deadline at the same moment (it is derived from the shared
    // activity timestamp). Exactly one tab performs the logout; the others are informed by
    // the AUTH_LOGOUT broadcast. Without Web Locks, authService.logout() de-duplicates the
    // server revocation itself.
    const runLogout = () => {
      authService.logout();
      finishLocally();
    };
    const locks = typeof navigator !== "undefined" ? navigator.locks : null;
    if (locks && typeof locks.request === "function") {
      locks
        .request(INACTIVITY_LOGOUT_LOCK, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            logAuthDiagnostic("inactivity_logout_delegated_to_other_tab");
            // The owning tab's broadcast will clear this tab; apply locally as well in
            // case it is delayed, without a second server request.
            authService.syncFromStorage();
            finishLocally();
            return;
          }
          runLogout();
          // Hold the lock briefly so tabs reaching the same deadline see it as taken.
          await new Promise((resolve) => setTimeout(resolve, 1500));
        })
        .catch(runLogout);
    } else {
      runLogout();
    }
  }, [navigate, onLogout, timeoutMs, dismissWarning]);

  // Handle 1-second countdown ticking while warning modal is active
  useEffect(() => {
    if (!isWarningOpen) return;

    const tick = () => {
      if (!countdownEndRef.current) return;
      const remaining = Math.max(0, Math.ceil((countdownEndRef.current - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining <= 0) {
        performLogout();
      }
    };

    const timerId = setInterval(tick, 1000);
    return () => clearInterval(timerId);
  }, [isWarningOpen, performLogout]);

  useEffect(() => {
    // Only monitor when a user is actively authenticated and timeout is configured
    if (!user || !authService.isAuthenticated() || !timeoutMs) {
      isLoggingOutRef.current = false;
      dismissWarning();
      return;
    }

    // Initialize shared activity timestamp if missing
    try {
      if (!localStorage.getItem(ACTIVITY_STORAGE_KEY)) {
        localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()));
      }
    } catch {
      /* ignore */
    }

    const checkInactivity = () => {
      if (!authService.isAuthenticated()) {
        dismissWarning();
        return;
      }

      // EXCEPTION 1: Current tab is inside Connect / Meet or in an active call/huddle
      if (isUserInActiveConnectMeet(location.pathname)) {
        logAuthDiagnostic("inactivity_check_exempt_local_connect_meet", {
          pathname: location.pathname,
        });
        dismissWarning();
        recordActivity(true);
        return;
      }

      // EXCEPTION 2: Another tab in the browser has an active Connect meeting lease
      if (hasActiveMeetingLease()) {
        logAuthDiagnostic("inactivity_check_exempt_cross_tab_active_meeting", {
          pathname: location.pathname,
        });
        dismissWarning();
        return;
      }

      // Read authoritative latest shared activity time across all tabs
      const lastActivityTime = getSharedLastActivityTime();
      const elapsed = Date.now() - lastActivityTime;

      if (elapsed < timeoutMs) {
        // Activity detected (in this tab or another) -> ensure warning is cleared
        dismissWarning();
        return;
      }

      if (elapsed >= timeoutMs) {
        // Inactivity threshold reached -> display warning with 60-second countdown if not already showing
        if (!isWarningOpenRef.current) {
          logAuthDiagnostic("inactivity_warning_displayed", {
            elapsed,
            timeoutMs,
            pathname: location.pathname,
          });
          setIsWarningOpen(true);
          isWarningOpenRef.current = true;
          // Derive the deadline from the SHARED activity time, not from when this tab
          // happened to notice, so every tab shows the same countdown and ends together.
          countdownEndRef.current = getInactivityDeadline(lastActivityTime, timeoutMs, Date.now());
          setCountdown(Math.max(0, Math.ceil((countdownEndRef.current - Date.now()) / 1000)));
        }
      }
    };

    const handleUserInteraction = () => {
      if (isWarningOpenRef.current) {
        // User interacted while warning is visible -> treat as renewed activity and dismiss warning
        stayLoggedIn();
      } else {
        recordActivity(false);
      }
    };

    const interactionEvents = [
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "pointerdown",
      "mousemove",
    ];

    interactionEvents.forEach((evt) => {
      window.addEventListener(evt, handleUserInteraction, { passive: true });
    });

    window.addEventListener("focus", checkInactivity);
    document.addEventListener("visibilitychange", checkInactivity);

    // Cross-tab broadcast listener for immediate synchronization
    const unsubscribeAuthSync = subscribeAuthSync((event) => {
      if (event.type === AUTH_SYNC_EVENTS.ACTIVITY_UPDATED) {
        // Another tab reported user activity -> reset local evaluation and dismiss warning
        lastThrottleWriteRef.current = event.lastActivity || Date.now();
        dismissWarning();
      } else if (event.type === AUTH_SYNC_EVENTS.AUTH_LOGOUT) {
        dismissWarning();
        if (typeof onLogout === "function") {
          onLogout();
        }
      }
    });

    // Fallback cross-tab storage event listener
    const onStorage = (e) => {
      if (e.key === ACTIVITY_STORAGE_KEY) {
        lastThrottleWriteRef.current = Number(e.newValue) || Date.now();
        dismissWarning();
      }
    };
    window.addEventListener("storage", onStorage);

    const intervalId = setInterval(checkInactivity, INACTIVITY_CHECK_INTERVAL_MS);

    return () => {
      interactionEvents.forEach((evt) => {
        window.removeEventListener(evt, handleUserInteraction);
      });
      window.removeEventListener("focus", checkInactivity);
      document.removeEventListener("visibilitychange", checkInactivity);
      window.removeEventListener("storage", onStorage);
      unsubscribeAuthSync();
      clearInterval(intervalId);
    };
  }, [user, location.pathname, timeoutMs, recordActivity, performLogout, onLogout, dismissWarning, stayLoggedIn]);

  return {
    isWarningOpen,
    countdown,
    stayLoggedIn,
  };
}

export default useInactivityTimeout;
